/**
 * Tarsius Web WASM Module Loader
 * Handles loading and initialization of the Emscripten-compiled WASM module
 */

export interface TarsiusWasmModule {
  ScTarsiusWebRenderer: any;
  GeoPoint2D: any;
  GeoPoint3D: any;
  DrawingSize3D: any;
  Pen: any;
  Brush: any;
  Draw2DIn3DStyle: any;
  TextStyle: any;
  AnchorLocation: any;
  Oval: any;
  ModelProperties: any;
  DrawingContext3D: any;
  VectorGeoPoint2D: any;
  VectorGeoPoint3D: any;
  VectorDrawingSize3D: any;
  VectorInt: any;
  PenCapStyle: any;
  PenJoinStyle: any;
  AnchorRelativeTo: any;
  AxisDirection: any;
  // Allow indexing for dynamic property access
  [key: string]: any;
}

let wasmModuleInstance: TarsiusWasmModule | null = null;
let loadingPromise: Promise<TarsiusWasmModule> | null = null;

/**
 * Load the Tarsius WASM module
 * This function is idempotent - it will only load the module once
 * @param webgpuDevice Optional WebGPU device for Dawn WebGPU API
 */
export async function loadTarsiusWasm(webgpuDevice?: GPUDevice): Promise<TarsiusWasmModule> {
  // Return cached instance if already loaded
  if (wasmModuleInstance) {
    console.log('WASM module already loaded, returning cached instance');
    return wasmModuleInstance;
  }

  // Return existing loading promise if currently loading
  if (loadingPromise) {
    console.log('WASM module loading in progress, waiting...');
    return loadingPromise;
  }

  // Start loading
  console.log('Loading Tarsius WASM module...');

  loadingPromise = (async () => {
    try {
      // Load the Emscripten-generated JS file as an ES6 module
      const scriptUrl = '/wasm/scTarsiusWeb.js';
      
      console.log(`Loading WASM module from: ${scriptUrl}`);
      
      // Dynamically import the module
      const wasmModule = await import(/* webpackIgnore: true */ scriptUrl);
      
      // Get the createTarsiusModule function
      const createModule = wasmModule.default || wasmModule.createTarsiusModule;
      
      if (!createModule) {
        throw new Error('createTarsiusModule function not found in module');
      }

      console.log('WASM module script loaded, initializing...');

      // Initialize the WASM module
      const moduleConfig: any = {
        locateFile: (path: string) => {
          // Locate .wasm and .data files (workers are inline with ES6 modules)
          if (path.endsWith('.wasm')) {
            console.log(`Locating WASM file: /wasm/${path}`);
            return `/wasm/${path}`;
          }
          if (path.endsWith('.data')) {
            console.log(`Locating data file: /wasm/${path}`);
            return `/wasm/${path}`;
          }
          return path;
        },

        // Callback when module is ready
        onRuntimeInitialized: function () {
          console.log('WASM runtime initialized');
/*          
          // Debug: Check filesystem contents
          try {
            const FS = (this as any).FS;
            if (FS) {
              console.log('📁 Virtual Filesystem Debug:');
              console.log('  Root:', FS.readdir('/'));
              
              // Try to find proj.db in common locations
              const searchPaths = [
                '/usr/local/share/proj',
                '/share/proj',
                '/proj',
                '/usr/share/proj'
              ];
              
              for (const path of searchPaths) {
                try {
                  const contents = FS.readdir(path);
                  console.log(`  ${path}:`, contents);
                  
                  // Check for proj.db specifically
                  const projDbPath = `${path}/proj.db`;
                  const analysis = FS.analyzePath(projDbPath);
                  if (analysis.exists) {
                    const stats = FS.stat(projDbPath);
                    console.log(`  ✅ Found proj.db at ${projDbPath} (${stats.size} bytes)`);
                  }
                } catch (e) {
                  console.log(`  ${path}: not found`);
                }
              }
            }
          } catch (e) {
            console.warn('Could not inspect filesystem:', e);
          }
*/
        },

        // Print handler
        print: (text: string) => {
          console.log('[WASM stdout]', text);
        },

        // Error handler
        printErr: (text: string) => {
          console.error('[WASM stderr]', text);
        },
        
        // Unhandled exception handler
        onAbort: (what: any) => {
          console.error('[WASM ABORT]', what);
        },

        // For pthreads support
        mainScriptUrlOrBlob: '/wasm/scTarsiusWeb.js',
      };

      // Pass WebGPU device to module if provided (required for Dawn WebGPU API)
      if (webgpuDevice) {
        moduleConfig.preinitializedWebGPUDevice = webgpuDevice;
        console.log('✅ WebGPU device passed to WASM module configuration');
      }

      const module = await createModule(moduleConfig);

      console.log('Tarsius WASM module loaded successfully');
      console.log('Available classes:', Object.keys(module).filter(k => typeof module[k] === 'function'));

      wasmModuleInstance = module as TarsiusWasmModule;
      return wasmModuleInstance;

    } catch (error) {
      console.error('Failed to load Tarsius WASM module:', error);
      loadingPromise = null; // Reset so we can try again
      throw error;
    }
  })();

  return loadingPromise;
}

/**
 * Check if the WASM module is loaded
 */
export function isWasmLoaded(): boolean {
  return wasmModuleInstance !== null;
}

/**
 * Get the loaded WASM module (throws if not loaded)
 */
export function getWasmModule(): TarsiusWasmModule {
  if (!wasmModuleInstance) {
    throw new Error('WASM module not loaded. Call loadTarsiusWasm() first.');
  }
  return wasmModuleInstance;
}

/**
 * Unload the WASM module (for cleanup/testing)
 */
export function unloadWasm(): void {
  wasmModuleInstance = null;
  loadingPromise = null;
  console.log('WASM module unloaded');
}

